<template>
  <div class="Users">
    <b-row class="mb-4 align-items-end">
      <b-col class="text-left">
        <h1>Utilisateurs</h1>
      </b-col>

      <b-col cols="2" class="text-right"
        ><b-button
          variant="outline-primary"
          @click="openNewUserModal"
          :disabled="!canCreateUserTenant"
          ><i class="fas fa-user-plus"></i>&nbsp; Ajouter un
          utilisateur</b-button
        ></b-col
      >
    </b-row>
    <b-overlay :show="isLoading" spinner-variant="primary" opacity="1">
      <CustomTable
        class="Users-list p-0 text-left"
        v-if="canSearchTenantUsers"
        filterable
        :items="users"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :total-rows="totalRows"
        :tableOptions="tableOptions"
        @filtered="onFilterChanged"
        @sort-changed="onSortChanged"
        @page-changed="onPageChanged"
        @per-page-changed="onPerPageChanged"
      >
        <template v-slot:cell(profiles)="data">
          <div v-for="tenant in data.item.tenants" :key="tenant.name">
            <template v-if="tenant.name === selectedTenant.name">
              <b-badge
                pill
                class="mr-1"
                variant="primary"
                v-for="profile in tenant.profiles"
                :key="`${tenant.name}-${profile}`"
                >{{ profile }}</b-badge
              >
            </template>
          </div>
        </template>

        <template v-slot:cell(residencies)="data">
          <div v-if="data.item.canViewAllResidencies">Toutes</div>
          <div v-else v-for="(r, i) in data.item.residencies" :key="i">
            {{ getAssignedResidencies(r).map((rr) => rr._source.model)[0] }}
          </div>
        </template>

        <template v-slot:cell(actions)="data">
          <div class="text-center">
            <a
              href="#"
              title="Delete user"
              v-if="canDeleteUser"
              :data-cy="`delete-user-${data.item._id}`"
              @click="deleteUser(data.item._id)"
            >
              <i class="fas fa-trash" />
            </a>
            &nbsp;
            <a
              href="#"
              title="Edit user content"
              v-if="canAdministrateUserTenants"
              :data-cy="`view-user-content-${data.item._id}`"
              @click="showUserContentModal(data.item._id)"
            >
              <i class="fas fa-edit" /> </a
            >&nbsp;
            <a
              href="#"
              title="Manage user profiles"
              v-if="canAdministrateUserTenants"
              :data-cy="`view-user-tenant-${data.item._id}`"
              @click="showUserTenantModal(data.item._id)"
            >
              <i class="fas fa-user-cog" />
            </a>
          </div>
        </template>
        <template #empty>
          <h4 class="text-center mt-3">Aucun utilisateur trouvé.</h4>
        </template>
      </CustomTable>
      <ListNotAllowed v-else />
    </b-overlay>
    <user-profiles-modal
      v-if="currentViewedUser"
      :user="currentViewedUser"
      :profiles="profiles"
      :isLoading="isLoading"
      :selectedTenant="selectedTenant"
      @remove-profile="onRemoveProfile"
      @add-profile="onAddProfile"
      @modal-close="currentViewedUser = { _source: {} }"
    />
    <user-content-modal
      :user="currentViewedUser"
      :isLoading="isLoading"
      :profiles="profiles"
      :selectedTenant="selectedTenant"
      @create="onCreateUser"
      @update="onUpdateUser"
      @modal-close="currentViewedUser = { _source: {} }"
    />
  </div>
</template>

<script>
import UserProfilesModal from '@/components/users/UserProfilesModal';
import UserContentModal from '@/components/users/UserContentModal';
import ListNotAllowed from '@/components/ListNotAllowed';
import CustomTable from '@/components/CustomTable';
import { mapGetters } from 'vuex';
import { isActionAllowed } from '@/helpers/rights.js';
import _ from 'lodash';

export default {
  name: 'Users',
  components: {
    UserContentModal,
    UserProfilesModal,
    ListNotAllowed,
    CustomTable,
  },

  data() {
    return {
      rights: [],
      isLoading: false,
      users: [],
      profiles: [],
      userMapping: null,
      filter: null,
      minimumFilterLength: 3,
      currentViewedUser: { _source: {}, credentials: {} },
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      sort: null,
      tableOptions: {},
      fields: [
        {
          key: 'firstName',
          label: 'Prénom',
          sortable: true,
        },
        {
          key: 'lastName',
          label: 'Nom',
          sortable: true,
        },
        {
          key: 'email',
          label: 'Email',
          sortable: true,
        },
        {
          key: 'phoneNumber',
          label: 'Téléphone',
          sortable: true,
        },
        {
          key: 'profiles',
          label: 'Profiles',
          sortable: false,
        },
        {
          key: 'residencies',
          label: 'Résidences',
          sortable: false,
        },
        {
          key: 'actions',
          label: '',
          sortable: false,
        },
      ],
      filerableFields: [
        'lastName.text',
        'firstName.text',
        'email.text',
        'phoneNumber.text',
      ],
    };
  },

  computed: {
    ...mapGetters('tenants', ['tenants', 'selectedTenant', 'softTenants']),
    ...mapGetters('auth', ['currentUserRights', 'currentUser']),
    canSearchTenantUsers() {
      return (
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/user',
          'search',
          this.selectedTenant.index
        ) &&
        isActionAllowed(
          this.currentUserRights,
          'security',
          'getUserMapping',
          this.selectedTenant.index
        )
      );
    },
    canAdministrateUserTenants() {
      return (
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/user',
          'addProfile',
          this.selectedTenant.index
        ) &&
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/user',
          'listProfiles',
          this.selectedTenant.index
        ) &&
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/user',
          'removeProfile',
          this.selectedTenant.index
        ) &&
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/user',
          'get',
          this.selectedTenant.index
        ) &&
        isActionAllowed(
          this.currentUserRights,
          'security',
          'getUserMapping',
          this.selectedTenant.index
        )
      );
    },
    canCreateUserTenant() {
      return (
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/user',
          'create',
          this.selectedTenant.index
        ) &&
        isActionAllowed(
          this.currentUserRights,
          'security',
          'getCredentials',
          this.selectedTenant.index
        ) &&
        isActionAllowed(
          this.currentUserRights,
          'security',
          'updateCredentials',
          this.selectedTenant.index
        )
      );
    },
    canDeleteUser() {
      return isActionAllowed(
        this.currentUserRights,
        'multi-tenancy/user',
        'delete',
        this.selectedTenant.index
      );
    },
  },

  async mounted() {
    const promises = [];

    if (this.canSearchTenantUsers) {
      promises.push(this.fetchUserMapping(), this.fetchUsers());
    }

    if (this.canAdministrateUserTenants) {
      promises.push(this.fetchProfiles());
    }

    await Promise.all(promises);
  },

  methods: {
    getAssignedResidencies(residency) {
      return this.softTenants.filter((st) =>
        st._id === residency ? true : false
      );
    },
    async fetchUserMapping() {
      this.isLoading = true;

      try {
        const response = await this.$kuzzle.security.getUserMapping();
        this.userMapping = response.mapping;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(
          'Désolé, une erreur est survenue lors de la récupération des informations des utilisatateurs.'
        );
      }

      this.isLoading = false;
    },
    async fetchUsers() {
      if (!this.selectedTenant) {
        return;
      }

      const users = [];
      const body = {
        query: {
          bool: {},
        },
      };

      body.sort = this.sort || { _id: { order: 'asc' } };

      if (this.filter) {
        body.query = {
          multi_match: {
            query: this.filter,
            type: 'phrase_prefix',
            fields: this.filerableFields,
          },
        };
      }

      const request = {
        controller: 'multi-tenancy/user',
        action: 'search',
        tenantId: this.selectedTenant._id,
        from: (this.currentPage - 1) * this.perPage,
        size: this.perPage,
        lang: 'elasticsearch',
        body,
      };

      try {
        this.isLoading = true;

        const response = await this.$kuzzle.query(request);

        this.totalRows = response.result.total;
        for (const user of response.result.hits) {
          users.push({
            _id: user._id,
            ...user._source,
          });
        }

        this.users = users;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(
          'Désolé, une erreur est survenue lors de la récupération de la liste des utilisatateurs.'
        );
      }

      this.isLoading = false;
    },
    async fetchProfiles() {
      this.isLoading = true;

      try {
        const response = await this.$kuzzle.query({
          controller: 'multi-tenancy/user',
          action: 'listProfiles',
          tenantId: this.selectedTenant._id,
        });

        this.profiles = response.result.profiles;
        if (!this.currentUser.content.profileIds.includes('admin')) {
          this.profiles = this.profiles.filter((profile) => profile != 'admin');
        }
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(
          "Désolé, une erreur est survenue lors de la récupération des profils de l'utilisatateur."
        );
      }

      this.isLoading = false;
    },
    async onAddProfile(payload) {
      this.isLoading = true;
      try {
        await this.$kuzzle.query({
          controller: 'pvstudio/keycloak',
          action: 'addProfile',
          ...payload,
        });
        await this.fetchUsers();
        this.currentViewedUser = await this.getUser(this.currentViewedUser._id);
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(
          "Désolé, une erreur est survenue lors de la modification des profils de l'utilisatateur."
        );
      }

      this.isLoading = false;
    },
    async onRemoveProfile(payload) {
      this.isLoading = true;

      const tenant = this.tenants.find((el) => el.name === payload.tenantName);

      if (!tenant) {
        const error = `Cannot find a tenant with the name "${payload.tenantName}".`;

        this.isLoading = false;
        this.$log.error(error);
        this.showErrorToast(
          "Désolé, une erreur est survenue lors de la modification des profils de l'utilisatateur."
        );
        return;
      }

      payload.tenantId = tenant._id;
      delete payload.tenantName;
      try {
        await this.$kuzzle.query({
          controller: 'pvstudio/keycloak',
          action: 'removeProfile',
          ...payload,
        });
        await this.fetchUsers();
        this.currentViewedUser = await this.getUser(this.currentViewedUser._id);
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async onFilterChanged(value) {
      this.filter = value;
      await this.fetchUsers();
    },
    async onSortChanged(value) {
      this.sort = {
        [value.sortBy]: { order: value.sortDesc ? 'desc' : 'asc' },
      };
      await this.fetchUsers();
    },
    async onPageChanged(value) {
      this.currentPage = value;
      await this.fetchUsers();
    },
    async onPerPageChanged(value) {
      this.perPage = value;
      await this.fetchUsers();
    },
    onTenantSelected(value) {
      this.selectedTenantId = value;
    },
    async onCreateUser(payload) {
      this.isLoading = true;

      try {
        await this.$kuzzle.query({
          controller: 'pvstudio/keycloak',
          action: 'createUser',
          body: payload,
        });

        await this.fetchUsers();
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async onUpdateUser(payload) {
      this.isLoading = true;

      try {
        const _id = payload._id;
        const tenantId = this.selectedTenant._id;
        const body = payload._source;

        await this.$kuzzle.query({
          controller: 'multi-tenancy/user',
          action: 'update',
          tenantId,
          _id,
          body,
        });
        await this.$kuzzle.query({
          controller: 'pvstudio/keycloak',
          action: 'syncKeycloakUser',
          tenantId,
          _id,
          body,
        });

        await this.fetchUsers();
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(
          "Désolé, une erreur est survenue lors de la modification de l'utilisatateur."
        );
      }

      this.isLoading = false;
    },
    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Erreur',
        variant: 'danger',
        toaster: 'b-toaster-bottom-right',
        appendToast: true,
        dismissible: true,
        noAutoHide: true,
        'auto-hide-delay': '10s',
      });
    },
    async getUser(userId) {
      try {
        const response = await this.$kuzzle.query({
          controller: 'multi-tenancy/user',
          action: 'get',
          tenantId: this.selectedTenant._id,
          _id: userId,
        });

        return response.result;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(
          "Désolé, une erreur est survenue lors de la récupération des informations de l'utilisatateur."
        );
      }
    },
    async getUserCredentials(userId) {
      try {
        const credentials = await this.$kuzzle.security.getCredentials(
          KEYCLOAK_STRATEGY.FRONTEND,
          userId
        );

        return credentials;
      } catch (error) {
        // @todo : use error code instead of status
        // when https://github.com/kuzzleio/kuzzle/issues/1988 is resolved
        if (error.status === 412) {
          return null;
        }

        this.$log.error(error);
        this.showErrorToast(
          "Désolé, une erreur est survenue lors de la récupération des indentifiants de l'utilisatateur."
        );
      }
    },
    handleDeleteConfirmation(userId) {
      const customMessage = this.$createElement('p', [
        'Etes-vous sûr de vouloir supprimer cet user?',
        this.$createElement('code', userId),
      ]);
      return this.$bvModal.msgBoxConfirm(customMessage);
    },
    async deleteUser(userId) {
      const isDeleteConfirmed = await this.handleDeleteConfirmation(userId);

      if (!isDeleteConfirmed) {
        return;
      }

      this.isLoading = true;

      try {
        await this.$kuzzle.query({
          controller: 'pvstudio/keycloak',
          action: 'deleteUser',
          tenantId: this.selectedTenant._id,
          _id: userId,
        });

        await this.fetchUsers();
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(
          "Désolé, une erreur est survenue lors de la suppression de l'utilisatateur."
        );
      }

      this.isLoading = false;
    },
    openNewUserModal() {
      this.currentViewedUser = {
        content: {},
      };
      this.$bvModal.show('user-content-modal');
    },
    async showUserTenantModal(userId) {
      this.currentViewedUser = await this.getUser(userId);
      this.$bvModal.show('user-tenants-modal');
    },
    async showUserContentModal(userId) {
      const [user, credentials] = await Promise.all([
        this.getUser(userId),
        this.getUserCredentials(userId),
      ]);

      if (user) {
        this.currentViewedUser = user;
        this.currentViewedUser.credentials = credentials;

        this.$bvModal.show('user-content-modal');
      }
    },
  },

  watch: {
    selectedTenant() {
      if (this.canSearchTenantUsers) {
        this.fetchUsers();
        this.fetchProfiles();
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
